import React, { useState, useEffect } from 'react';

function JoystickUI({ cameraController, disabled = false }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [joystickActive, setJoystickActive] = useState(false);
  const [joystickPosition, setJoystickPosition] = useState({ x: 0, y: 0 });
  const [joystickStart, setJoystickStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!cameraController) return;

    // CRITICAL: Always define handlers so cleanup can reference them
    const handleTouchStart = (e) => {
      // When disabled, do NOTHING - let touches pass through
      if (disabled) return;

      const touch = e.touches[0];
      // Check if in bottom-center joystick area
      const centerX = window.innerWidth / 2;
      const isInJoystickArea = Math.abs(touch.clientX - centerX) < 100 && touch.clientY > window.innerHeight - 200;

      // Only activate if in joystick area (no preventDefault since we use passive listeners)
      if (isInJoystickArea) {
        cameraController.joystickTouchId = touch.identifier;
        cameraController.joystickStart = { x: touch.clientX, y: touch.clientY };
        cameraController.joystickActive = true;
        setJoystickActive(true);
        setJoystickStart({ x: touch.clientX, y: touch.clientY });
        setJoystickPosition({ x: 0, y: 0 });
      }
    };

    const handleTouchMove = (e) => {
      if (disabled) return;

      if (cameraController.joystickActive) {
        cameraController.updateJoystickVector(e);
        setJoystickPosition({
          x: cameraController.joystickVector.x * 30,
          y: cameraController.joystickVector.y * 30
        });
      }
    };

    const handleTouchEnd = (e) => {
      if (disabled) return;

      const ended = cameraController.endJoystickTouch(e);
      if (ended) {
        setJoystickActive(false);
        setJoystickPosition({ x: 0, y: 0 });
      }
    };

    // Always use passive listeners so they never block modal/UI interactions
    const listenerOptions = { passive: true };

    window.addEventListener('touchstart', handleTouchStart, listenerOptions);
    window.addEventListener('touchmove', handleTouchMove, listenerOptions);
    window.addEventListener('touchend', handleTouchEnd, listenerOptions);

    return () => {
      // MUST remove with same options to properly unregister
      window.removeEventListener('touchstart', handleTouchStart, listenerOptions);
      window.removeEventListener('touchmove', handleTouchMove, listenerOptions);
      window.removeEventListener('touchend', handleTouchEnd, listenerOptions);
    };
  }, [cameraController, disabled]);

  // Disable active joystick when disabled prop changes (modal opens)
  useEffect(() => {
    if (disabled && joystickActive) {
      if (cameraController) {
        cameraController.joystickActive = false;
        cameraController.joystickTouchId = null;
      }
      setJoystickActive(false);
      setJoystickPosition({ x: 0, y: 0 });
    }
  }, [disabled, joystickActive, cameraController]);

  if (!isMobile) return null;

  return (
    <>
      {/* Virtual Joystick Visual (when active) */}
      {joystickActive && (
        <div
          style={{
            position: 'fixed',
            left: joystickStart.x,
            top: joystickStart.y,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 100,
          }}
        >
          {/* Joystick base */}
          <div
            style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.4)',
              position: 'relative',
            }}
          >
            {/* Joystick knob */}
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: `translate(calc(-50% + ${joystickPosition.x}px), calc(-50% + ${joystickPosition.y}px))`,
              }}
            />
          </div>
        </div>
      )}

      {/* Joystick touch area indicator - only show when enabled */}
      {!disabled && (
        <div
          style={{
            position: 'fixed',
            left: '50%',
            bottom: '20px',
            transform: 'translateX(-50%)',
            width: '160px',
            height: '160px',
            borderRadius: '50%',
            border: '3px solid rgba(255, 255, 255, 0.4)',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          {/* Arrow indicators - 8 directions */}
          <svg width="120" height="120" viewBox="0 0 120 120" style={{ position: 'absolute' }}>
            {/* Up arrow */}
            <path d="M60 20 L50 35 L70 35 Z" fill="rgba(255,255,255,0.5)" />
            {/* Down arrow */}
            <path d="M60 100 L50 85 L70 85 Z" fill="rgba(255,255,255,0.5)" />
            {/* Left arrow */}
            <path d="M20 60 L35 50 L35 70 Z" fill="rgba(255,255,255,0.5)" />
            {/* Right arrow */}
            <path d="M100 60 L85 50 L85 70 Z" fill="rgba(255,255,255,0.5)" />
            {/* Up-Right arrow - pointing to top-right corner */}
            <path d="M88 32 L78 35 L85 42 Z" fill="rgba(255,255,255,0.5)" />
            {/* Up-Left arrow - pointing to top-left corner */}
            <path d="M32 32 L35 42 L42 35 Z" fill="rgba(255,255,255,0.5)" />
            {/* Down-Right arrow - pointing to bottom-right corner */}
            <path d="M88 88 L85 78 L78 85 Z" fill="rgba(255,255,255,0.5)" />
            {/* Down-Left arrow - pointing to bottom-left corner */}
            <path d="M32 88 L42 85 L35 78 Z" fill="rgba(255,255,255,0.5)" />
          </svg>
        </div>
      )}
    </>
  );
}

export default JoystickUI;
